// ─────────────────────────────────────────
//  FULL-STACK BUILD (Phase 4 — Pro+)
// ─────────────────────────────────────────

import { S, sb } from './state.js';
import { showToast, extractCode } from './utils.js';
import { aiRequest } from './ai.js';
import { applyCode, notifyCodeUpdate } from './editor.js';
import { runCode } from './preview.js';
import { addMsg, showSteps, updateStep, hideSteps, updateStatusPill, removeTyping } from './ui.js';
import { saveConversation } from './conversations.js';

// ── Module state ──
export let _generatedFiles = {};
export let _generatedSpec = {};
export let _fullStackPrompt = '';
export let _fullStackMode = false;

// Getter for cross-module access (avoids circular import issues with live bindings)
export function isFullStackMode() { return _fullStackMode; }

// ── Cloud sync detection ──
const CLOUD_SYNC_APPS = [
  /booking|appointment|reservation/i,
  /inventory|stock|warehouse/i,
  /order|delivery|dispatch/i,
  /customer.*manage|client.*manage/i,
  /multi.?user|team|staff|employee/i,
  /real.?time|live\s+update/i,
  /payment|checkout|e.?commerce/i,
  /crm|erp/i,
];

export function isProPlus() {
  return S.isAdmin || S.tier === 'pro_plus';
}

export function showProPlusUpgrade() {
  const modal = document.getElementById('pro-plus-modal');
  if (modal) modal.classList.add('open');
}

export function checkCloudSyncNeeded() {
  const firstMsg = S.history.find(m => m.role === 'user')?.content || '';
  const needsCloud = CLOUD_SYNC_APPS.some(p => p.test(firstMsg));
  if (needsCloud && S.code) {
    setTimeout(() => addCloudSyncNotice(isProPlus()), 1500);
  }
}

function addCloudSyncNotice(hasAccess) {
  const msgs = document.getElementById('messages');
  if (!msgs) return;
  if (document.getElementById('cloud-sync-notice')) return;
  const notice = document.createElement('div');
  notice.id = 'cloud-sync-notice';
  notice.className = 'msg assistant';

  if (hasAccess) {
    notice.innerHTML = `
      <div class="msg-label">KasiCode AI</div>
      <div class="msg-bubble" style="background:rgba(0,217,126,.06);border-color:rgba(0,217,126,.3);">
        <div style="font-weight:700;margin-bottom:6px;">💡 Ready to upgrade this to full-stack?</div>
        <div style="font-size:13px;color:var(--muted);line-height:1.6;margin-bottom:12px;">
          Your app works great on this device! To make it work for <strong>multiple users</strong> across all devices (customers booking from their phones, data syncing everywhere), I can upgrade it to full-stack with a real database — keeping your existing design intact.
        </div>
        <div style="display:flex;gap:8px;flex-wrap:wrap;">
          <button onclick="toggleFullStackMode(); this.closest('.msg').remove();" style="padding:8px 16px;background:var(--green);border:none;border-radius:8px;color:#000;font-family:var(--font);font-weight:700;font-size:12px;cursor:pointer;">🗄️ Upgrade to Full-Stack</button>
          <button onclick="this.closest('.msg').remove()" style="padding:8px 16px;background:transparent;border:1px solid var(--border2);border-radius:8px;color:var(--muted);font-family:var(--font);font-size:12px;cursor:pointer;">Keep as single-device</button>
        </div>
      </div>`;
  } else {
    notice.innerHTML = `
      <div class="msg-label">KasiCode AI</div>
      <div class="msg-bubble" style="background:rgba(255,107,53,.08);border-color:rgba(255,107,53,.3);">
        <div style="font-weight:700;margin-bottom:6px;">⚡ This app needs cloud sync to work fully</div>
        <div style="font-size:13px;color:var(--muted);line-height:1.6;margin-bottom:12px;">
          Right now this app stores data on <strong>this device only</strong>. For a booking or inventory app to work properly, you need:<br>
          • Customers to book from <em>their own phones</em><br>
          • Data to sync across all your devices<br>
          • Real-time updates when new bookings come in
        </div>
        <div style="display:flex;gap:8px;flex-wrap:wrap;">
          <button onclick="showProPlusUpgrade()" style="padding:8px 16px;background:var(--orange);border:none;border-radius:8px;color:#fff;font-family:var(--font);font-weight:700;font-size:12px;cursor:pointer;">🚀 Upgrade to Pro+ — Full-Stack Mode</button>
          <button onclick="this.closest('.msg').remove()" style="padding:8px 16px;background:transparent;border:1px solid var(--border2);border-radius:8px;color:var(--muted);font-family:var(--font);font-size:12px;cursor:pointer;">Keep as demo</button>
        </div>
      </div>`;
  }

  msgs.appendChild(notice);
  msgs.scrollTop = msgs.scrollHeight;
}

export function fullStackBuild(userMsg) {
  removeTyping();

  const hasExistingApp = S.code && S.code.trim().length > 100;

  if (hasExistingApp) {
    // Upgrading existing app — communicate clearly
    showSteps(['Analyzing your app', 'Designing backend', 'Generating full-stack project', 'Ready to download']);
    updateStep(0, 'active');
    updateStatusPill('Analyzing your existing app...');
    addMsg('assistant', `🗄️ **Upgrading your app to full-stack** — I'll keep your existing UI and add a real Supabase backend with multi-user support, cloud sync, and proper authentication.\n\nYour current app's design and features will be preserved and enhanced.`, false);
  } else {
    showSteps(['Designing architecture', 'Generating database schema', 'Writing all files', 'Ready to download']);
    updateStep(0, 'active');
    updateStatusPill('Designing your app...');
    addMsg('assistant', `🗄️ Full-Stack Mode — building a complete Next.js app with Supabase backend for: "${userMsg.slice(0, 80)}..."`, false);
  }

  _fullStackPrompt = userMsg;

  // Refresh session to ensure token is valid, then start the build
  sb.auth.getSession()
    .then(({ data }) => {
      const freshSession = data?.session;
      const authToken = freshSession?.access_token || S.session?.access_token;
      if (!authToken) {
        addMsg('assistant', '⚠️ Session expired. Please sign out and sign in again.', false);
        hideSteps();
        return;
      }

      fetch('/api/fullstack/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + authToken,
        },
        body: JSON.stringify({
          prompt: userMsg,
          apiKey: S.key || null,
          existingCode: S.code || null,
        }),
      })
        .then(res => {
          if (!res.ok) {
            return res.json().then(err => {
              if (err.code === 'PRO_PLUS_REQUIRED') {
                showProPlusUpgrade();
              } else {
                addMsg('assistant', '⚠️ Full-stack generation failed: ' + (err.error || 'Unknown error'), false);
              }
              hideSteps();
            });
          }

          // Parse SSE stream
          const reader = res.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';

          function readChunk() {
            reader.read().then(({ done, value }) => {
              if (done) return;

              buffer += decoder.decode(value, { stream: true });
              const lines = buffer.split('\n');
              buffer = lines.pop() || '';

              for (const line of lines) {
                if (!line.startsWith('data: ')) continue;
                try {
                  const event = JSON.parse(line.slice(6));
                  handleFullStackEvent(event);
                } catch (_) {}
              }

              readChunk();
            }).catch(() => {
              addMsg('assistant', '⚠️ Stream interrupted. Check your connection.', false);
              hideSteps();
            });
          }

          readChunk();
        })
        .catch(() => {
          addMsg('assistant', '⚠️ Full-stack generation failed. Check your connection.', false);
          hideSteps();
        });
    })
    .catch(() => {
      addMsg('assistant', '⚠️ Session error. Please sign out and sign in again.', false);
      hideSteps();
    });
}

export function handleFullStackEvent(event) {
  switch (event.step) {
    case 'architect':
      updateStatusPill(event.message);
      break;

    case 'architect_done':
      _generatedSpec = event.spec;
      updateStep(0, 'done');
      updateStep(1, 'active');
      updateStatusPill('Generating database schema...');
      break;

    case 'schema_done':
      updateStep(1, 'done');
      updateStep(2, 'active');
      updateStatusPill('Writing all project files...');
      break;

    case 'coding_done':
      _generatedFiles = event.files || {};
      updateStep(2, 'done');
      updateStep(3, 'active');
      updateStatusPill('Packaging your project...');
      break;

    case 'complete':
      _generatedFiles = event.files || {};
      _generatedSpec = event.spec || {};
      updateStep(3, 'done');
      updateStatusPill('Generating preview...');

      // Generate a client-side preview of the main page
      generateFullStackPreview(_fullStackPrompt, event.spec);
      break;

    case 'error':
      addMsg('assistant', '⚠️ ' + event.message, false);
      hideSteps();
      break;
  }
}

export function generateFullStackPreview(userMsg, spec) {
  const previewPrompt = `You are a UI designer. Generate a SINGLE self-contained HTML file that shows a beautiful, realistic preview/mockup of this app's main interface. This is a PREVIEW ONLY — use localStorage for any data, make all buttons and forms work visually.

The app: ${userMsg}
${spec?.features ? 'Features: ' + spec.features.join(', ') : ''}
${spec?.pages ? 'Pages: ' + spec.pages.map(p => p.name).join(', ') : ''}

Rules:
- Single HTML file with all CSS and JS inline
- Beautiful, modern, mobile-first design
- Show the main dashboard/home page with realistic sample data
- All buttons and forms should work (use localStorage)
- Include navigation showing all pages (even if they just show a placeholder view)
- Use professional fonts from Google Fonts CDN
- Make it look like a real production app
- Put your code in a \`\`\`html code block`;

  aiRequest(previewPrompt, [
    { role: 'user', content: previewPrompt }
  ], 'coder')
    .then(previewReply => {
      if (previewReply) {
        const previewCode = extractCode(previewReply);
        if (previewCode) {
          S.code = previewCode;
          applyCode(previewCode);
          runCode();
          notifyCodeUpdate();
        }
      }
      updateStatusPill('Done!');
      showFullStackResult({ files: _generatedFiles, spec: _generatedSpec });
    })
    .catch(() => {
      // Preview generation failed — not critical
      updateStatusPill('Done!');
      showFullStackResult({ files: _generatedFiles, spec: _generatedSpec });
    });
}

export function showFullStackResult(event) {
  const spec = event.spec || {};
  const fileCount = Object.keys(event.files || {}).length;
  const appName = spec.appName || 'Your App';

  const wrap = document.createElement('div');
  wrap.className = 'msg assistant';
  wrap.innerHTML = `
    <div class="msg-label">KasiCode AI</div>
    <div class="msg-bubble" style="background:rgba(0,217,126,.06);border-color:rgba(0,217,126,.3);">
      <div style="font-weight:800;font-size:16px;margin-bottom:8px;">✅ ${appName} is ready!</div>
      <div style="font-size:13px;color:var(--muted);margin-bottom:12px;line-height:1.6;">
        Generated <strong>${fileCount} files</strong> — a complete Next.js project with Supabase backend.
        ${spec.features ? '<br>Features: ' + spec.features.slice(0, 3).join(', ') : ''}
      </div>
      <div style="display:flex;flex-direction:column;gap:8px;">
        <button onclick="downloadFullStackZip()" style="padding:12px;background:var(--green);border:none;border-radius:8px;color:#000;font-family:var(--font);font-weight:700;font-size:13px;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:6px;">
          📦 Download ZIP (${fileCount} files)
        </button>
        <button onclick="pushToGitHub()" style="padding:12px;background:#24292f;border:1px solid #444;border-radius:8px;color:#fff;font-family:var(--font);font-weight:700;font-size:13px;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:6px;">
          🐙 Push to GitHub → Deploy on Vercel
        </button>
        <button onclick="showFullStackFiles()" style="padding:10px;background:transparent;border:1px solid var(--border2);border-radius:8px;color:var(--muted);font-family:var(--font);font-size:12px;cursor:pointer;">
          📁 Browse files
        </button>
      </div>
      <div style="font-size:11px;color:var(--muted2);margin-top:10px;line-height:1.5;">
        📋 <strong>Deploy in 3 steps:</strong><br>
        1. Run the SQL in your <a href="https://supabase.com/dashboard" target="_blank" style="color:var(--accent);">Supabase SQL Editor</a><br>
        2. Push to GitHub (button above) and create a repo<br>
        3. Import on <a href="https://vercel.com/new" target="_blank" style="color:var(--accent);">Vercel</a>, add your env vars, deploy — live in 2 minutes!
      </div>
    </div>`;

  const msgs = document.getElementById('messages');
  if (msgs) {
    msgs.appendChild(wrap);
    msgs.scrollTop = 99999;
  }

  S.builds++;
  saveConversation();
}

export function showFullStackFiles() {
  const files = _generatedFiles;
  const fileList = Object.keys(files).sort().join('\n');
  addMsg('assistant', `📁 Generated files (${Object.keys(files).length} total):\n\n${fileList}`, false);
}

export function toggleFullStackMode() {
  if (!isProPlus()) {
    showProPlusUpgrade();
    return;
  }
  _fullStackMode = !_fullStackMode;
  const btn = document.getElementById('fullstack-btn');
  if (_fullStackMode) {
    if (btn) btn.classList.add('active');
    if (btn) btn.title = 'Full-Stack Mode ON — click to switch to client-side';
    showToast('🗄️ Full-Stack Mode ON — next build will generate a complete Next.js project');
  } else {
    if (btn) btn.classList.remove('active');
    if (btn) btn.title = 'Full-Stack Mode (Pro+)';
    showToast('Switched to client-side mode');
  }
}

export function showFullStackButton() {
  if (isProPlus()) {
    const btn = document.getElementById('fullstack-btn');
    if (btn) btn.style.display = 'flex';
    const uploadBtn = document.getElementById('upload-folder-btn');
    if (uploadBtn) uploadBtn.style.display = 'flex';
  }
}

// ─────────────────────────────────────────
//  FOLDER UPLOAD (Pro+ Feature)
// ─────────────────────────────────────────

// Uploaded project files (separate from AI-generated files)
export let _uploadedFiles = {};
export let _uploadedProjectName = '';

const IGNORED_DIRS = ['node_modules', '.next', '.git', 'dist', 'build', '.vercel', '.turbo', '__pycache__', '.cache'];
const MAX_FILE_SIZE = 512 * 1024; // 512KB per file
const MAX_TOTAL_FILES = 500;

export function openUploadFolder() {
  if (!isProPlus()) {
    showProPlusUpgrade();
    return;
  }
  const modal = document.getElementById('upload-folder-modal');
  if (modal) modal.classList.add('open');
  // Reset state
  resetUploadUI();
}

export function closeUploadFolder(e) {
  const modal = document.getElementById('upload-folder-modal');
  if (!e || e.target === modal) {
    if (modal) modal.classList.remove('open');
  }
}

function resetUploadUI() {
  const progress = document.getElementById('upload-progress');
  const result = document.getElementById('upload-result');
  const actions = document.getElementById('upload-actions');
  if (progress) progress.style.display = 'none';
  if (result) result.style.display = 'none';
  if (actions) actions.style.display = 'none';
  const bar = document.getElementById('upload-bar');
  if (bar) bar.style.width = '0%';
}

export async function handleFolderUpload(fileList) {
  if (!fileList || fileList.length === 0) return;

  resetUploadUI();
  const progress = document.getElementById('upload-progress');
  const status = document.getElementById('upload-status');
  const bar = document.getElementById('upload-bar');
  if (progress) progress.style.display = 'block';

  _uploadedFiles = {};
  _uploadedProjectName = '';

  // Filter out ignored directories and large files
  const validFiles = [];
  for (const file of fileList) {
    const path = file.webkitRelativePath || file.name;
    const parts = path.split('/');

    // Skip ignored directories
    const isIgnored = parts.some(part => IGNORED_DIRS.includes(part));
    if (isIgnored) continue;

    // Skip files that are too large
    if (file.size > MAX_FILE_SIZE) continue;

    // Skip binary files (images, fonts, etc.) — only text-based files
    const ext = file.name.split('.').pop().toLowerCase();
    const textExts = ['js', 'jsx', 'ts', 'tsx', 'json', 'html', 'css', 'scss', 'less', 'md', 'txt', 'sql', 'env', 'yaml', 'yml', 'toml', 'xml', 'svg', 'sh', 'bash', 'py', 'rb', 'go', 'rs', 'prisma', 'graphql', 'gql', 'vue', 'svelte', 'astro', 'mjs', 'cjs', 'lock'];
    const noExtFiles = ['.gitignore', '.env', '.env.local', '.env.example', 'Dockerfile', 'Makefile', 'Procfile'];
    const isText = textExts.includes(ext) || noExtFiles.includes(file.name);
    if (!isText) continue;

    validFiles.push(file);
    if (validFiles.length >= MAX_TOTAL_FILES) break;
  }

  if (validFiles.length === 0) {
    showToast('⚠ No valid text files found in the folder');
    if (progress) progress.style.display = 'none';
    return;
  }

  // Detect project name from the root folder
  const firstPath = validFiles[0].webkitRelativePath || '';
  _uploadedProjectName = firstPath.split('/')[0] || 'uploaded-project';

  // Read all files
  const total = validFiles.length;
  let loaded = 0;

  if (status) status.textContent = `Reading ${total} files...`;

  for (const file of validFiles) {
    try {
      const content = await readFileAsText(file);
      // Store with path relative to the project root (strip the root folder name)
      const fullPath = file.webkitRelativePath || file.name;
      const parts = fullPath.split('/');
      const relativePath = parts.slice(1).join('/'); // Remove root folder name
      if (relativePath) {
        _uploadedFiles[relativePath] = content;
      }
    } catch (_) {
      // Skip files that can't be read as text
    }

    loaded++;
    if (bar) bar.style.width = Math.round((loaded / total) * 100) + '%';
    if (status) status.textContent = `Reading files... (${loaded}/${total})`;
  }

  // Done — show result
  if (progress) progress.style.display = 'none';

  const fileCount = Object.keys(_uploadedFiles).length;
  if (fileCount === 0) {
    showToast('⚠ Could not read any files from the folder');
    return;
  }

  // Detect project type
  const hasPackageJson = 'package.json' in _uploadedFiles;
  const hasNextConfig = 'next.config.js' in _uploadedFiles || 'next.config.ts' in _uploadedFiles || 'next.config.mjs' in _uploadedFiles;
  const hasSupabase = Object.keys(_uploadedFiles).some(f => f.includes('supabase'));
  const hasSql = Object.keys(_uploadedFiles).some(f => f.endsWith('.sql'));

  let projectType = 'Web Project';
  if (hasNextConfig) projectType = 'Next.js Project';
  else if (hasPackageJson) {
    try {
      const pkg = JSON.parse(_uploadedFiles['package.json']);
      if (pkg.dependencies?.react) projectType = 'React Project';
      else if (pkg.dependencies?.vue) projectType = 'Vue Project';
      else if (pkg.dependencies?.svelte) projectType = 'Svelte Project';
      else projectType = 'Node.js Project';
    } catch (_) {}
  }

  const result = document.getElementById('upload-result');
  const resultInfo = document.getElementById('upload-result-info');
  const actions = document.getElementById('upload-actions');

  if (result) result.style.display = 'block';
  if (resultInfo) {
    resultInfo.innerHTML = `
      <strong>${_uploadedProjectName}</strong> — ${projectType}<br>
      ${fileCount} files loaded
      ${hasSupabase ? ' · <span style="color:var(--green);">Has Supabase config</span>' : ''}
      ${hasSql ? ' · <span style="color:var(--accent);">Has SQL migrations</span>' : ''}
    `;
  }
  if (actions) actions.style.display = 'flex';

  showToast(`✓ Loaded ${fileCount} files from ${_uploadedProjectName}`);
}

function readFileAsText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
  });
}

export function previewUploadedProject() {
  if (!Object.keys(_uploadedFiles).length) {
    showToast('⚠ No files loaded');
    return;
  }

  // Close the modal
  const modal = document.getElementById('upload-folder-modal');
  if (modal) modal.classList.remove('open');

  // Try to find an index.html or a main page to preview
  const previewFile =
    _uploadedFiles['index.html'] ||
    _uploadedFiles['public/index.html'] ||
    _uploadedFiles['app/page.tsx'] ||
    _uploadedFiles['app/page.jsx'] ||
    _uploadedFiles['pages/index.tsx'] ||
    _uploadedFiles['pages/index.jsx'] ||
    _uploadedFiles['src/App.tsx'] ||
    _uploadedFiles['src/App.jsx'] ||
    null;

  if (previewFile && (previewFile.includes('<html') || previewFile.includes('<!DOCTYPE'))) {
    // It's a full HTML file — render directly
    applyCode(previewFile);
    runCode();
    notifyCodeUpdate();
  } else {
    // Generate a file browser preview
    const fileList = Object.keys(_uploadedFiles).sort();
    const previewHtml = generateFileBrowserPreview(_uploadedProjectName, fileList, _uploadedFiles);
    applyCode(previewHtml);
    runCode();
    notifyCodeUpdate();
  }

  // Show result message in chat
  addMsg('assistant', `📂 Loaded **${_uploadedProjectName}** (${Object.keys(_uploadedFiles).length} files). You can browse the files, push to GitHub, or deploy on Vercel.\n\n**Next steps:**\n1. Run the SQL in Supabase (if your project has migrations)\n2. Push to GitHub using the 🐙 button\n3. Import the repo on Vercel and add your env vars`, false);

  // Also populate the fullstack generated files so download/push work
  Object.assign(_generatedFiles, _uploadedFiles);
  if (_generatedSpec) {
    _generatedSpec.appName = _uploadedProjectName;
  }
}

function generateFileBrowserPreview(projectName, fileList, files) {
  // HTML escape helper
  function esc(str) {
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  // Build a tree structure
  const tree = {};
  for (const path of fileList) {
    const parts = path.split('/');
    let current = tree;
    for (let i = 0; i < parts.length - 1; i++) {
      if (!current[parts[i]]) current[parts[i]] = {};
      current = current[parts[i]];
    }
    current[parts[parts.length - 1]] = null; // leaf = file
  }

  function renderTree(obj, indent = 0) {
    let html = '';
    const entries = Object.entries(obj).sort(([a, av], [b, bv]) => {
      // Folders first
      if (av !== null && bv === null) return -1;
      if (av === null && bv !== null) return 1;
      return a.localeCompare(b);
    });
    for (const [name, value] of entries) {
      const pad = '  '.repeat(indent);
      if (value !== null) {
        html += `${pad}📁 ${esc(name)}\n`;
        html += renderTree(value, indent + 1);
      } else {
        html += `${pad}📄 ${esc(name)}\n`;
      }
    }
    return html;
  }

  const treeText = renderTree(tree);
  const readmeContent = files['README.md'] || files['readme.md'] || '';
  const pkgJson = files['package.json'] ? JSON.parse(files['package.json']) : null;
  const safeName = esc(projectName);

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>${safeName} — Project Browser</title>
<link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;800&family=JetBrains+Mono:wght@400;500&display=swap" rel="stylesheet">
<style>
* { margin:0; padding:0; box-sizing:border-box; }
body { font-family:'Inter',sans-serif; background:#0f0e1a; color:#e8e6f0; padding:24px; min-height:100vh; }
.header { margin-bottom:24px; }
.header h1 { font-size:1.5rem; font-weight:800; margin-bottom:4px; }
.header p { color:#8b8a9e; font-size:13px; }
.badge { display:inline-block; padding:2px 8px; border-radius:4px; font-size:11px; font-weight:600; margin-right:6px; }
.badge-green { background:rgba(0,217,126,.15); color:#00d97e; }
.badge-blue { background:rgba(99,102,241,.15); color:#6366f1; }
.section { background:#1a1930; border:1px solid #2a2945; border-radius:12px; padding:16px; margin-bottom:16px; }
.section-title { font-weight:700; font-size:14px; margin-bottom:12px; display:flex; align-items:center; gap:8px; }
pre { font-family:'JetBrains Mono',monospace; font-size:12px; line-height:1.6; overflow-x:auto; white-space:pre; color:#a5a3b8; }
.readme { font-size:13px; line-height:1.7; color:#c5c3d8; }
.readme h1, .readme h2, .readme h3 { margin:12px 0 6px; color:#e8e6f0; }
.readme code { background:#2a2945; padding:1px 5px; border-radius:3px; font-family:'JetBrains Mono',monospace; font-size:12px; }
.stats { display:flex; gap:16px; flex-wrap:wrap; margin-top:8px; }
.stat { font-size:12px; color:#8b8a9e; }
.stat strong { color:#e8e6f0; }
</style>
</head>
<body>
<div class="header">
  <h1>📂 ${safeName}</h1>
  <p>${pkgJson?.description ? esc(pkgJson.description) : 'Uploaded project'}</p>
  <div style="margin-top:8px;">
    ${pkgJson ? '<span class="badge badge-green">Node.js</span>' : ''}
    ${files['next.config.js'] || files['next.config.ts'] || files['next.config.mjs'] ? '<span class="badge badge-blue">Next.js</span>' : ''}
    <span class="badge badge-green">${fileList.length} files</span>
  </div>
</div>

<div class="section">
  <div class="section-title">🗂️ File Tree</div>
  <pre>${treeText}</pre>
</div>

${readmeContent ? `<div class="section"><div class="section-title">📖 README</div><div class="readme">${readmeContent.slice(0, 2000).replace(/</g, '&lt;').replace(/>/g, '&gt;')}</div></div>` : ''}

${pkgJson ? `<div class="section">
  <div class="section-title">📦 package.json</div>
  <div class="stats">
    <div class="stat"><strong>${Object.keys(pkgJson.dependencies || {}).length}</strong> dependencies</div>
    <div class="stat"><strong>${Object.keys(pkgJson.devDependencies || {}).length}</strong> devDependencies</div>
    ${pkgJson.scripts ? `<div class="stat"><strong>${Object.keys(pkgJson.scripts).length}</strong> scripts</div>` : ''}
  </div>
</div>` : ''}

<div class="section" style="background:rgba(255,107,53,.05);border-color:rgba(255,107,53,.3);">
  <div class="section-title">🚀 Deploy Steps</div>
  <ol style="font-size:13px;line-height:2;padding-left:20px;color:#c5c3d8;">
    <li>Run the SQL migration in your <strong>Supabase SQL Editor</strong></li>
    <li>Push to <strong>GitHub</strong> (use the 🐙 button in the toolbar)</li>
    <li>Import the repo on <strong>Vercel</strong> and add your environment variables</li>
    <li>Deploy — your app will be live in ~2 minutes!</li>
  </ol>
</div>
</body>
</html>`;
}

export function pushUploadedToGitHub() {
  // Reuse the existing pushToGitHub logic by populating _generatedFiles
  if (!Object.keys(_uploadedFiles).length) {
    showToast('⚠ No files loaded');
    return;
  }

  // Close modal
  const modal = document.getElementById('upload-folder-modal');
  if (modal) modal.classList.remove('open');

  // Populate the shared generated files state
  Object.keys(_generatedFiles).forEach(k => delete _generatedFiles[k]);
  Object.assign(_generatedFiles, _uploadedFiles);
  _generatedSpec.appName = _uploadedProjectName;
  _generatedSpec.description = 'Uploaded project — ' + _uploadedProjectName;

  // Trigger the existing push flow (via window global to avoid circular import)
  if (window.pushToGitHub) {
    window.pushToGitHub();
  } else {
    showToast('⚠ Push function not available');
  }
}

export function downloadUploadedZip() {
  if (!Object.keys(_uploadedFiles).length) {
    showToast('⚠ No files loaded');
    return;
  }

  showToast('📦 Preparing ZIP...');
  fetch('/api/fullstack/download', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      files: _uploadedFiles,
      appName: _uploadedProjectName,
    }),
  })
    .then(res => res.blob())
    .then(blob => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = _uploadedProjectName.toLowerCase().replace(/\s+/g, '-') + '.zip';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      showToast('✓ Downloaded!');
    })
    .catch(() => {
      showToast('⚠ Download failed');
    });
}
